import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Shell from "@/components/Shell";
import ProgressHeader from "@/components/flow/ProgressHeader";
import QuestionStep from "@/components/flow/QuestionStep";
import ResultView from "@/components/flow/ResultView";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";
import { toast } from "sonner";
import * as flow from "@/lib/flow";
import { markCheckin } from "@/lib/reminders";
import { track } from "@/lib/analytics";

export default function Navigate() {
  const { content, contentError, reloadContent, deviceId, refreshProgress, setFocusMode } = useApp();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState("q1"); // q1 | q2 | q3 | result | baseline
  const [input, setInput] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [result, setResult] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [baselineFactor, setBaselineFactor] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep("q1"); setInput(null); setCtx(null); setResult(null);
    setBaseline(null); setBaselineFactor(null);
    if (params.get("baseline")) setParams({});
  };

  const startBaseline = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/checkin", {
        device_id: deviceId,
        internal_condition_input: "steady",
        reassess_of: params.get("reassess") || null,
      });
      setBaseline({ ...data.baseline, checkin_id: data.checkin_id, transition: data.transition });
      setStep("baseline");
    } catch (e) { toast("Something went wrong"); }
    setLoading(false);
  };

  useEffect(() => {
    localStorage.setItem("ss_seen_intro", "true");
  }, []);

  // Hide bottom nav during the focused check-in flow (ResultView owns the 'result' step).
  useEffect(() => {
    if (step === "result") return;
    setFocusMode(["q1", "q2", "q3", "baseline"].includes(step));
  }, [step, setFocusMode]);
  useEffect(() => () => setFocusMode(false), [setFocusMode]);

  useEffect(() => {
    if (params.get("baseline") && content && step === "q1" && !baseline) startBaseline();
    // eslint-disable-next-line
  }, [params, content]);

  const submit = async (timeId) => {
    setLoading(true);
    try {
      const data = await flow.submitCheckin({ deviceId, input, ctx, time: { id: timeId }, content, reassessOf: params.get("reassess") });
      data._ctxId = ctx.id;
      data._timeId = timeId;
      setResult(data);
      setStep("result");
    } catch (e) { toast("Something went wrong"); }
    setLoading(false);
  };

  if (!content) {
    return (
      <Shell>
        <div className="px-6 pt-24 text-center" data-testid="content-status">
          {contentError ? (
            <>
              <h2 className="font-serif-display text-3xl">Can't reach the server</h2>
              <p className="mt-3 text-[14px] text-[#474747]">
                You may be offline. Check your connection and try again.
              </p>
              <button
                data-testid="content-retry"
                onClick={reloadContent}
                className="mt-6 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus"
              >
                Try again
              </button>
            </>
          ) : (
            <p className="text-[#6B6B6B] font-mono-ss text-sm">Loading…</p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell className="ss-dark">
      {step === "q1" && !params.get("baseline") && (
        <>
          <ProgressHeader index={1} total={3} onBack={() => window.history.back()} />
          <QuestionStep
            testidPrefix="q1"
            headline="What feels most true right now?"
            instruction="Choose the one that feels closest. It does not have to describe everything."
            options={content.inputs}
            onSelect={(o) => { setInput(o); track("checkin_started"); setStep("q2"); }}
          />
          <div className="px-6 -mt-16 pb-28">
            <button
              data-testid="q1-steady"
              onClick={startBaseline}
              className="w-full text-left ss-card ss-tap ss-focus p-5 border-dashed"
            >
              <div className="text-[17px] font-medium">{content.baseline_input.title}</div>
              <div className="mt-1.5 text-[13.5px] text-[#6B6B6B]">{content.baseline_input.support}</div>
            </button>
          </div>
        </>
      )}

      {step === "q2" && (
        <>
          <ProgressHeader index={2} total={3} onBack={() => setStep("q1")} />
          <QuestionStep
            testidPrefix="q2"
            headline="What do you need to do next?"
            instruction="The right response depends partly on what the next part of your day is asking from you."
            options={content.contexts.map((c) => ({ id: c.id, title: c.label, support: c.support }))}
            onSelect={(o) => { setCtx(o); setStep("q3"); }}
          />
        </>
      )}

      {step === "q3" && (
        <>
          <ProgressHeader index={3} total={3} onBack={() => setStep("q2")} />
          <QuestionStep
            testidPrefix="q3"
            headline="How much space do you have right now?"
            instruction="Choose what is realistic — not what would be ideal."
            options={content.times.map((t) => ({ id: t.id, title: t.label }))}
            onSelect={(o) => submit(o.id)}
          />
        </>
      )}

      {loading && (
        <div className="px-6 py-10 text-center text-[#6B6B6B] font-mono-ss text-sm">Reading your responses…</div>
      )}

      {step === "result" && result && (
        <ResultView initial={result} onRestart={reset} />
      )}

      {step === "baseline" && baseline && (
        <BaselineResult baseline={baseline} content={content} deviceId={deviceId}
          onDone={() => { refreshProgress(); reset(); }} />
      )}
    </Shell>
  );
}

function BaselineResult({ baseline, content, onDone }) {
  const [factor, setFactor] = useState(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    try {
      await http.post(`/checkin/${baseline.checkin_id}/feedback`, {
        attempted: "yes",
        reported_change: factor,
      });
    } catch (e) {}
    markCheckin();
    setSaved(true);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-6 pt-14 pb-28">
      {baseline.transition && (
        <div className="ss-card p-4 border-l-2 border-[#7fa98c] mb-5" data-testid="result-transition">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B6B6B]">Since your last check</span>
          <p className="mt-1.5 text-[14px] text-[#1A1A1A]">
            {baseline.transition.from_label} → <span className="font-medium">{baseline.transition.to_label}</span>
            {" · "}
            {{ eased: "that eased", similar: "about the same", heavier: "a heavier reading", shifted: "a different reading" }[baseline.transition.moved]}
          </p>
        </div>
      )}
      <h1 className="font-serif-display text-4xl leading-tight">{baseline.headline}</h1>
      <p className="mt-4 text-[15px] text-[#474747] leading-relaxed">{baseline.observation}</p>

      {!saved ? (
        <>
          <p className="mt-8 text-[15px] font-medium">{baseline.question}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {baseline.factors.map((f) => (
              <button
                key={f.id}
                data-testid={`baseline-factor-${f.id}`}
                onClick={() => setFactor(f.id)}
                className={`rounded-full px-4 py-2.5 text-[14px] border ss-tap ss-focus ${
                  factor === f.id ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#EBE9E4] bg-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            data-testid="baseline-record"
            onClick={save}
            className="mt-10 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            Record this baseline
          </button>
        </>
      ) : (
        <div className="mt-10">
          <p className="text-[15px] text-[#474747]">Baseline recorded. Protect what is working.</p>
          <button
            data-testid="baseline-done"
            onClick={onDone}
            className="mt-6 w-full border border-[#1A1A1A] rounded-full px-6 py-4 font-medium ss-tap ss-focus"
          >
            Done for now
          </button>
        </div>
      )}
    </motion.div>
  );
}
